import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, Calendar, FileText, Brain, CheckSquare as CheckSquareIcon, BookOpen, Upload, PenTool, Link as LinkIcon, AlertCircle, XCircle, RotateCcw, MessageSquare, Paperclip, Eye, Edit, ChevronDown, ChevronUp, Users, Settings } from 'lucide-react';
import FileViewer from './FileViewer';
import toast from 'react-hot-toast';
import QuizViewer from './QuizViewer';
import TrainingViewer from './TrainingViewer';
import ChecklistViewer from './ChecklistViewer';
import CombinedTaskViewer from './CombinedTaskViewer';
import ProofFileUpload from './ProofFileUpload';
import SignatureSection from './SignatureSection';
import api from "../../utils/api"

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center gap-2 font-semibold text-gray-700">
                    {Icon && <Icon className="w-5 h-5" />}
                    {title}
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            {isOpen && (
                <div className="p-4 bg-white border-t border-gray-200">
                    {children}
                </div>
            )}
        </div>
    );
};

const TaskDetailModal = ({ isOpen, onClose, task, onComplete, onRefresh, isAdmin = false, onEdit, isPreview = false }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [responseData, setResponseData] = useState('');
    const [proofUrl, setProofUrl] = useState('');
    const [proofFiles, setProofFiles] = useState([]);
    const [signatureData, setSignatureData] = useState('');
    // Assignment Details State
    const [quizScore, setQuizScore] = useState(null);
    const [viewTracked, setViewTracked] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    // Assignment Details State
    const [assignmentDetails, setAssignmentDetails] = useState([]);
    const [totalAssignmentCount, setTotalAssignmentCount] = useState(0);
    const [areAssignmentsLoading, setAreAssignmentsLoading] = useState(false);

    // Fetch detailed assignments for Admin view
    useEffect(() => {
        if (isOpen && isAdmin && !isPreview && task && task.id) {
            setAssignmentDetails([]);
            setTotalAssignmentCount(0);
            fetchAssignmentDetails(5);
        }
    }, [isOpen, isAdmin, task]);

    const fetchAssignmentDetails = async (limit = null) => {
        if (!task.id) return;
        setAreAssignmentsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const limitParam = limit ? `?limit=${limit}` : '';
            const response = await api.get(`/tasks/${task.id}/assignments${limitParam}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setAssignmentDetails(response.data.assignments);
                setTotalAssignmentCount(response.data.total_count);
            }
        } catch (error) {
            console.error('Error fetching assignment details:', error);
        } finally {
            setAreAssignmentsLoading(false);
        }
    };

    // Track task view automatically when modal opens (only for employees)
    useEffect(() => {
        if (isOpen && task && task.id && !viewTracked && !isAdmin) {
            trackTaskView();
        }
    }, [isOpen, task, viewTracked, isAdmin]);

    const trackTaskView = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/employee/${task.id}/track-view`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            setViewTracked(true);
        } catch (error) {
            console.error('Error tracking task view:', error);
            // Don't show error to user - this is a background operation
        }
    };

    if (!isOpen || !task) return null;

    const getTaskIcon = (taskType) => {
        const iconClasses = "w-6 h-6";
        switch (taskType) {
            case 'quiz':
                return <Brain className={iconClasses} />;
            case 'training':
                return <BookOpen className={iconClasses} />;
            case 'checklist':
                return <CheckSquareIcon className={iconClasses} />;
            default:
                return <FileText className={iconClasses} />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high':
                return 'bg-red-100 text-red-700 border-red-200';
            case 'medium':
                return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            case 'low':
                return 'bg-blue-100 text-blue-700 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleStartTask = async () => {
        try {
            const token = localStorage.getItem('token');
            await api.put(
                `/tasks/employee/${task.id}/status`,
                { status: 'in_progress' },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            onRefresh();
        } catch (error) {
            console.error('Error starting task:', error);
            alert('Failed to start task');
        }
    };

    const handleCompleteTask = async (customResponseData = null, score = null) => {
        // Validate requirements
        if (task.requires_proof && proofFiles.length === 0 && !proofUrl) {
            toast.error('Please provide proof of completion (upload files or provide a link)');
            return;
        }

        if (task.requires_signature && !signatureData) {
            toast.error('Please provide your signature');
            return;
        }

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.post(
                `/tasks/employee/${task.id}/complete`,
                {
                    response_data: customResponseData || responseData,
                    proof_url: proofUrl,
                    proof_files: proofFiles.length > 0 ? proofFiles : null,
                    signature_data: signatureData,
                    score: score || quizScore,
                    quiz_grading_type: task.quiz_grading_type || 'auto',
                    passing_score: task.passing_score || 70
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            // Show appropriate message based on response
            if (response.data.message) {
                if (response.data.isPassed === false) {
                    toast(response.data.message, {
                        icon: '📊',
                        duration: 5000
                    });
                } else if (response.data.requiresReview) {
                    toast.success(response.data.message);
                } else {
                    toast.success(response.data.message);
                }
            } else {
                toast.success('Task completed successfully!');
            }

            onRefresh();
            onClose();
        } catch (error) {
            console.error('Error completing task:', error);
            toast.error('Failed to complete task');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequestRetake = async () => {
        setIsSubmitting(true);
        const loadingToast = toast.loading('Sending retake request...');
        try {
            const token = localStorage.getItem('token');
            await api.post(
                `/tasks/employee/${task.id}/request-retake`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            toast.success('Retake request sent successfully!', { id: loadingToast });
            onRefresh();
            onClose();
        } catch (error) {
            console.error('Error requesting retake:', error);
            toast.error(error.response?.data?.message || 'Failed to request retake', { id: loadingToast });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderTaskContent = () => {
        const isCompleted = task.status === 'completed';

        switch (task.task_type) {
            case 'quiz':
                return (
                    <QuizViewer
                        content={task.content}
                        gradingType={task.quiz_grading_type || 'auto'}
                        onComplete={(responseData, score) => {
                            setResponseData(responseData);
                            setQuizScore(score);
                            handleCompleteTask(responseData, score);
                        }}
                        isCompleted={isCompleted || isAdmin}
                        previousResponse={task.response_data}
                        taskStatus={task.status}
                        isAdmin={isAdmin}
                    />
                );

            case 'training':
                return (
                    <TrainingViewer
                        content={task.content}
                        onComplete={(responseData) => {
                            setResponseData(responseData);
                            handleCompleteTask(responseData);
                        }}
                        isCompleted={isCompleted || isAdmin}
                    />
                );

            case 'checklist':
                return (
                    <ChecklistViewer
                        content={task.content}
                        onComplete={(responseData) => {
                            setResponseData(responseData);
                            handleCompleteTask(responseData);
                        }}
                        isCompleted={isCompleted || isAdmin}
                        previousResponse={task.response_data}
                    />
                );

            case 'combined':
                return (
                    <CombinedTaskViewer
                        task={task}
                        isAdmin={isAdmin}
                    />
                );

            default:
                return (
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="flex items-center gap-3 mb-4">
                            <FileText className="w-6 h-6 text-gray-600" />
                            <h3 className="font-semibold text-gray-800">Task Details</h3>
                        </div>
                        <div className="prose prose-sm max-w-none text-gray-700">
                            {task.content ? (
                                <div dangerouslySetInnerHTML={{ __html: task.content.replace(/\n/g, '<br>') }} />
                            ) : (
                                <p>Task content will be displayed here</p>
                            )}
                        </div>
                    </div>
                );
        }
    };

    const isCompleted = task.status === 'completed';
    const isPendingReview = task.status === 'pending_review';
    const isRetakeRequested = task.status === 'retake_requested';
    const isFailed = task.status === 'failed';
    const isRejected = task.status === 'rejected';
    const canRetake = task.retake_allowed && (isFailed || isRejected);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
            <div className="bg-white rounded-none md:rounded-xl shadow-2xl max-w-[95vw] md:max-w-7xl w-full h-full md:h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 z-10">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 md:gap-4 flex-1">
                            <div className={`p-2 md:p-3 rounded-lg ${getPriorityColor(task.priority)} flex-shrink-0`}>
                                {getTaskIcon(task.task_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 md:mb-2 truncate">
                                    {task.title}
                                </h2>
                                <div className="flex flex-wrap gap-2">
                                    <span className={`px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold capitalize ${getPriorityColor(task.priority)}`}>
                                        {task.priority} Priority
                                    </span>
                                    <span className="px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold capitalize bg-purple-100 text-purple-700">
                                        {task.task_type}
                                    </span>
                                    {task.phase && (
                                        <span className="px-2 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold capitalize bg-primary/10 text-primary">
                                            {task.phase}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        >
                            <X className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                    {/* Description */}
                    {task.description && (
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
                            <p className="text-gray-700">{task.description}</p>
                        </div>
                    )}

                    {/* Task Attachments */}
                    {task.attachments && (Array.isArray(task.attachments) ? task.attachments : JSON.parse(task.attachments || '[]')).length > 0 && (
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Paperclip className="w-5 h-5 text-primary" />
                                <h3 className="font-semibold text-primary">Attachments</h3>
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                    {(Array.isArray(task.attachments) ? task.attachments : JSON.parse(task.attachments || '[]')).length} file(s)
                                </span>
                            </div>
                            <div className="space-y-2">
                                {(Array.isArray(task.attachments) ? task.attachments : JSON.parse(task.attachments || '[]')).map((file, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setPreviewFile(file)}
                                        className="w-full text-left flex items-center gap-3 p-3 bg-white hover:bg-primary/5 rounded-lg group transition-colors border border-primary/10"
                                    >
                                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                                            <FileText className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-800 truncate text-sm">
                                                {file.file_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Click to preview
                                            </p>
                                        </div>
                                        <Eye className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Calendar className="w-5 h-5 text-gray-600" />
                            <div>
                                <p className="text-xs text-gray-500">Due Date</p>
                                <p className="text-sm font-semibold text-gray-800">
                                    {formatDate(task.due_date)}
                                </p>
                            </div>
                        </div>

                        {task.estimated_duration && (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <Clock className="w-5 h-5 text-gray-600" />
                                <div>
                                    <p className="text-xs text-gray-500">Estimated Time</p>
                                    <p className="text-sm font-semibold text-gray-800">
                                        {task.estimated_duration} minutes
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-gray-600" />
                            <div>
                                <p className="text-xs text-gray-500">Status</p>
                                <p className="text-sm font-semibold text-gray-800 capitalize">
                                    {task.status === 'pending_review' ? 'Awaiting Review' :
                                        task.status === 'retake_requested' ? 'Retake Requested' :
                                            task.status}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Pending Review Banner */}
                    {isPendingReview && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-5 h-5 text-yellow-600" />
                                <h3 className="font-semibold text-yellow-800">Awaiting Admin Review</h3>
                            </div>
                            <p className="text-sm text-yellow-700">
                                Your submission is being reviewed by an administrator. You'll be notified once it's been graded.
                            </p>
                            {task.submitted_at && (
                                <p className="text-xs text-yellow-600 mt-2">
                                    Submitted on: {formatDate(task.submitted_at)}
                                </p>
                            )}

                            {/* Submission Details for Reviewer */}
                            {(task.proof_files || task.proof_url || task.signature_data) && (
                                <div className="mt-4 pt-4 border-t border-yellow-200">
                                    <h4 className="text-sm font-semibold text-yellow-800 mb-3">Submission Details</h4>

                                    {task.response_data && (
                                        <div className="mb-3">
                                            <p className="text-xs font-medium text-yellow-700 uppercase">Notes</p>
                                            <p className="text-sm text-gray-700 mt-1">{task.response_data}</p>
                                        </div>
                                    )}

                                    {task.proof_files && task.proof_files.length > 0 && (
                                        <div className="mb-3">
                                            <p className="text-xs font-medium text-yellow-700 uppercase mb-2">Proof Files</p>
                                            <div className="space-y-2">
                                                {(typeof task.proof_files === 'string' ? JSON.parse(task.proof_files) : task.proof_files).map((file, index) => (
                                                    <a
                                                        key={index}
                                                        href={file.file_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 p-2 bg-white border border-yellow-200 rounded text-sm hover:bg-yellow-50 transition-colors"
                                                    >
                                                        <FileText className="w-4 h-4 text-gray-500" />
                                                        <span className="text-gray-700">{file.file_name}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {task.proof_url && (
                                        <div className="mb-3">
                                            <p className="text-xs font-medium text-yellow-700 uppercase">Proof Link</p>
                                            <a
                                                href={task.proof_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-primary hover:underline mt-1 inline-block"
                                            >
                                                {task.proof_url}
                                            </a>
                                        </div>
                                    )}

                                    {task.signature_data && (
                                        <div>
                                            <p className="text-xs font-medium text-yellow-700 uppercase mb-2">Signature</p>
                                            <div className="border border-yellow-200 rounded-lg p-3 bg-white inline-block">
                                                {(task.signature_data.startsWith('http') || task.signature_data.startsWith('data:')) ? (
                                                    <img
                                                        src={task.signature_data}
                                                        alt="Signature"
                                                        className="max-h-24 max-w-full object-contain"
                                                    />
                                                ) : (
                                                    <p className="text-lg font-signature text-gray-800 px-2 py-1">
                                                        {task.signature_data}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Retake Requested Banner */}
                    {isRetakeRequested && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-5 h-5 text-orange-600" />
                                <h3 className="font-semibold text-orange-800">Retake Requested</h3>
                            </div>
                            <p className="text-sm text-orange-700">
                                Your request to retake this quiz has been sent to the administrator. You'll be notified once it's approved.
                            </p>
                        </div>
                    )}

                    {/* Completed Manual Quiz Results Banner */}
                    {isCompleted && task.quiz_grading_type === 'manual' && (task.score !== null || task.admin_feedback) && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <h3 className="font-semibold text-green-800">Quiz Reviewed & Approved</h3>
                            </div>

                            {task.score !== null && (
                                <div className="mb-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-medium text-green-800">Your Score:</span>
                                        <span className="text-2xl font-bold text-green-700">{task.score}%</span>
                                        {task.passing_score && (
                                            <span className="text-sm text-green-600">(Passing: {task.passing_score}%)</span>
                                        )}
                                    </div>
                                    <div className="w-full bg-green-200 rounded-full h-2">
                                        <div
                                            className="bg-green-600 h-2 rounded-full transition-all duration-500"
                                            style={{ width: `${Math.min(task.score, 100)}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}

                            {task.admin_feedback && (
                                <div className="mt-3 p-3 bg-white border border-green-200 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MessageSquare className="w-4 h-4 text-green-600" />
                                        <span className="text-sm font-semibold text-green-800">Admin Feedback</span>
                                    </div>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.admin_feedback}</p>
                                </div>
                            )}

                            {task.reviewed_at && (
                                <p className="text-xs text-green-600 mt-3">
                                    Reviewed on: {formatDate(task.reviewed_at)}
                                </p>
                            )}

                            {!task.retake_allowed && task.status !== 'retake_requested' && (
                                <button
                                    onClick={handleRequestRetake}
                                    disabled={isSubmitting}
                                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-white border border-green-600 text-green-700 hover:bg-green-50 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Request Retake
                                </button>
                            )}
                        </div>
                    )}

                    {/* Failed/Rejected Banner */}
                    {(isFailed || isRejected) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <XCircle className="w-5 h-5 text-red-600" />
                                <h3 className="font-semibold text-red-800">
                                    {isFailed ? 'Quiz Not Passed' : 'Submission Rejected'}
                                </h3>
                            </div>
                            {task.score !== null && (
                                <p className="text-sm text-red-700 mb-2">
                                    Your score: <span className="font-bold">{task.score}%</span>
                                    {task.passing_score && (
                                        <span> (Required: {task.passing_score}%)</span>
                                    )}
                                </p>
                            )}
                            {task.admin_feedback && (
                                <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <MessageSquare className="w-4 h-4 text-red-600" />
                                        <span className="text-sm font-medium text-red-800">Admin Feedback:</span>
                                    </div>
                                    <p className="text-sm text-gray-700">{task.admin_feedback}</p>
                                </div>
                            )}
                            {canRetake && (
                                <button
                                    onClick={handleStartTask}
                                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Retake Quiz
                                </button>
                            )}

                            {!canRetake && !task.retake_allowed && task.status !== 'retake_requested' && task.quiz_grading_type === 'manual' && (
                                <button
                                    onClick={handleRequestRetake}
                                    disabled={isSubmitting}
                                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-white border border-red-600 text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Request Retake
                                </button>
                            )}
                        </div>
                    )}

                    {/* Task Content */}
                    {renderTaskContent()}

                    {/* Admin: Collapsible Metadata Sections */}
                    {isAdmin && (
                        <div className="space-y-4 pt-4 border-t border-gray-100">
                            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Configuration Details</h4>

                            {/* Assignment Section */}
                            {!isPreview && (
                                <CollapsibleSection title="Assignment Details" icon={Users}>
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-xs text-gray-500 mb-1">Assignment Type</p>
                                                <p className="font-medium text-gray-800 capitalize">{task.assignment_type}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 mb-1">Target Audience</p>
                                                <p className="font-medium text-gray-800">
                                                    {task.assignment_type === 'all' && 'All Employees'}
                                                    {task.assignment_type === 'individual' && `${areAssignmentsLoading ? '...' : (totalAssignmentCount || task.assigned_employee_ids?.length || 0)} Specific Employee(s)`}
                                                    {task.assignment_type === 'role' && (task.assigned_roles?.length > 0 ? task.assigned_roles.join(', ') : 'All Roles')}
                                                    {task.assignment_type === 'team' && `${areAssignmentsLoading ? '...' : (totalAssignmentCount || task.assigned_team_ids?.length || 0)} Team(s)`}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Detailed lists for specific assignments */}
                                        {(task.assignment_type === 'role' || task.assignment_type === 'team' || task.assignment_type === 'individual') && (
                                            <div className="mt-3">
                                                <p className="text-xs text-gray-500 mb-2">
                                                    Assigned {task.assignment_type === 'individual' ? 'Employees' : task.assignment_type === 'team' ? 'Teams' : 'Roles'}
                                                </p>

                                                {areAssignmentsLoading && assignmentDetails.length === 0 ? (
                                                    <div className="text-xs text-gray-400 italic">Loading details...</div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <div className="flex flex-wrap gap-2">
                                                            {assignmentDetails.map((item, i) => (
                                                                <div key={item.id || i} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                                                                    {item.avatar ? (
                                                                        <img src={item.avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold">
                                                                            {item.name.charAt(0)}
                                                                        </div>
                                                                    )}
                                                                    <span className="text-sm text-gray-700">{item.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {assignmentDetails.length < totalAssignmentCount && (
                                                            <button
                                                                onClick={() => fetchAssignmentDetails(null)}
                                                                className="text-xs text-primary hover:text-primary-hover font-medium mt-2 flex items-center gap-1"
                                                            >
                                                                View all {totalAssignmentCount} recipients
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </CollapsibleSection>
                            )}

                            {/* Settings Section */}
                            <CollapsibleSection title="Task Settings & Requirements" icon={Settings}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${task.requires_proof ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                        <span className="text-sm text-gray-700">Requires Proof</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${task.requires_signature ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                        <span className="text-sm text-gray-700">Requires Signature</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${task.is_compliance_required ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                                        <span className="text-sm text-gray-700">Compliance Required</span>
                                    </div>
                                    {task.is_compliance_required && (
                                        <div>
                                            <p className="text-xs text-gray-500">Compliance Deadline</p>
                                            <p className="text-sm font-medium">{task.compliance_deadline_days} days</p>
                                        </div>
                                    )}
                                    {task.task_type === 'quiz' && (
                                        <>
                                            <div>
                                                <p className="text-xs text-gray-500">Grading Type</p>
                                                <p className="text-sm font-medium capitalize">{task.quiz_grading_type}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Passing Score</p>
                                                <p className="text-sm font-medium">{task.passing_score}%</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </CollapsibleSection>



                            {/* Schedule Section */}
                            {!isPreview && (
                                <CollapsibleSection title="Schedule & Timing" icon={Calendar}>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Scheduled For</p>
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                <p className="text-sm font-medium text-gray-800">
                                                    {task.scheduled_date ? formatDate(task.scheduled_date) : 'Immediately'}
                                                </p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Due Date Calculation</p>
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-gray-400" />
                                                <p className="text-sm font-medium text-gray-800">
                                                    {task.due_days_after_assignment} days after assignment
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </CollapsibleSection>
                            )}
                        </div>
                    )}

                    {/* Requirements Section - Only show if not completed and not admin */}
                    {!isCompleted && !isAdmin && (task.requires_proof || task.requires_signature) && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h3 className="font-semibold text-gray-800 mb-3">Requirements</h3>
                            <div className="space-y-4">
                                {task.requires_proof && (
                                    <div className="space-y-3">
                                        <ProofFileUpload
                                            proofFiles={proofFiles}
                                            onProofFilesChange={setProofFiles}
                                            proofUrl={proofUrl}
                                            onProofUrlChange={setProofUrl}
                                            isSubmitting={isSubmitting}
                                        />
                                    </div>
                                )}

                                {task.requires_signature && (
                                    <SignatureSection
                                        signatureData={signatureData}
                                        onSignatureChange={setSignatureData}
                                        isSubmitting={isSubmitting}
                                        error={!signatureData && isSubmitting ? "Signature is required" : null}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Response/Notes Section - Only show if not completed and not admin */}
                    {!isCompleted && !isAdmin && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                value={responseData}
                                onChange={(e) => setResponseData(e.target.value)}
                                placeholder="Add any notes or comments about this task..."
                                rows="3"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    )}

                    {/* Completion Info - Only show if completed */}
                    {isCompleted && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <h3 className="font-semibold text-green-800">Completed</h3>
                            </div>
                            <p className="text-sm text-gray-700">
                                Completed on: {formatDate(task.completed_at)}
                            </p>
                            {task.response_data && (
                                <div className="mt-3">
                                    <p className="text-sm font-medium text-gray-700">Your Notes:</p>
                                    <p className="text-sm text-gray-600 mt-1">{task.response_data}</p>
                                </div>
                            )}
                            {task.proof_files && task.proof_files.length > 0 && (
                                <div className="mt-3">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Submitted Proof Files:</p>
                                    <div className="space-y-2">
                                        {(typeof task.proof_files === 'string' ? JSON.parse(task.proof_files) : task.proof_files).map((file, index) => (
                                            <a
                                                key={index}
                                                href={file.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded text-sm hover:bg-gray-50 transition-colors"
                                            >
                                                <FileText className="w-4 h-4 text-gray-500" />
                                                <span className="text-gray-700">{file.file_name}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {task.signature_data && (
                                <div className="mt-3">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Signature:</p>
                                    <div className="border border-green-200 rounded-lg p-3 bg-white inline-block">
                                        {(task.signature_data.startsWith('http') || task.signature_data.startsWith('data:')) ? (
                                            <img
                                                src={task.signature_data}
                                                alt="Signature"
                                                className="max-h-24 max-w-full object-contain"
                                            />
                                        ) : (
                                            <p className="text-lg font-signature text-gray-800 px-2 py-1">
                                                {task.signature_data}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {task.proof_url && (
                                <div className="mt-3">
                                    <p className="text-sm font-medium text-gray-700">Proof Link:</p>
                                    <a
                                        href={task.proof_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-primary hover:underline mt-1 inline-block"
                                    >
                                        {task.proof_url}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                {
                    !isCompleted && !isPendingReview && (
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>

                            {isAdmin ? (
                                <button
                                    onClick={() => onEdit && onEdit(task)}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg transition-colors"
                                >
                                    <Edit className="w-4 h-4" />
                                    Edit Task
                                </button>
                            ) : (
                                <>
                                    {task.status === 'pending' && (
                                        <button
                                            onClick={handleStartTask}
                                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg transition-colors"
                                        >
                                            <Clock className="w-4 h-4" />
                                            Start Task
                                        </button>
                                    )}

                                    {/* Only show Complete button for regular tasks, not quiz/training/checklist (they have their own submit) */}
                                    {(task.status === 'in_progress' || task.status === 'pending') &&
                                        task.task_type === 'task' && (
                                            <button
                                                onClick={() => handleCompleteTask()}
                                                disabled={isSubmitting}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                            >
                                                <CheckCircle className="w-4 h-4" />
                                                {isSubmitting
                                                    ? 'Submitting...'
                                                    : task.requires_proof
                                                        ? 'Submit for Review'
                                                        : 'Complete Task'}
                                            </button>
                                        )}
                                </>
                            )}
                        </div>
                    )
                }

                {/* Close button only for pending review status */}
                {
                    isPendingReview && (
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    )
                }
            </div >

            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div >
    );
};

export default TaskDetailModal;

